import React from 'react';
import { Helix2000Character } from '../../../shared/CharacterTypes';
import { ProgressBar } from '../primitives/ProgressBar';
import { COLORS, SPACE, FONT_SIZE, FONT_WEIGHT } from '../theme';
import { usePlayerVital } from '../hooks/usePlayerVital';
import { readPlayerVitals } from '../../systems/player/livePlayerVitals';
import { HudGlyph } from './tome/hudOrnaments';
import type { HudConcreteStyle } from './useHudTomeVibe';

interface VitalsPanelProps {
  player: Helix2000Character;
  /** Out-of-combat HP regen is running (plan 03): the HP bar glows and says so. */
  regenerating?: boolean;
  /** Server says the player is in combat (no HP regen): a chip beside the HP label. */
  inCombat?: boolean;
  /** Active Tome HUD style direction ('a' | 'b' | 'c'). Undefined = classic mode. */
  hudStyle?: HudConcreteStyle;
}

/** Crossed blades drawn as SVG (vendored fonts lack the ⚔ glyph). */
const CombatChip: React.FC = () => (
  <span
    className="player-frame-combat hudt-chip"
    data-tone="combat"
    data-testid="player-frame-combat"
    title="In combat: no health regeneration until you stop fighting for a few seconds"
  >
    <svg aria-hidden width="9" height="9" viewBox="0 0 10 10">
      <path d="M1 1 L8 8 M2 9 L9 2 M6.5 9.5 L9.5 6.5 M0.5 3.5 L3.5 0.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
    In combat
  </span>
);

const sectionStyle: React.CSSProperties = { marginBottom: SPACE.sm };
const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: SPACE.xxs,
};
const labelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.xs,
  fontSize: FONT_SIZE.xs,
  color: COLORS.textSecondary,
};
const valuesStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  fontSize: FONT_SIZE.sm,
  fontWeight: FONT_WEIGHT.bold,
  color: COLORS.textPrimary,
};

const fill = (fraction: number): React.CSSProperties => ({
  transform: `scaleX(${Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0))})`,
});

/** One HP/MP/XP row: icon + label, current/max values, themed bar. */
const ResourceRow: React.FC<{
  icon: string; label: string; current: number; max: number;
  pct: number; color: string; height: number;
}> = ({ icon, label, current, max, pct, color, height }) => (
  <div style={sectionStyle}>
    <div style={headerStyle}>
      <div style={labelStyle}>
        <span aria-hidden>{icon}</span>
        <span>{label}</span>
      </div>
      <div style={valuesStyle}>
        <span>{current}</span>
        <span style={{ color: COLORS.textMuted }}>/</span>
        <span style={{ color: COLORS.textSecondary }}>{max}</span>
      </div>
    </div>
    <ProgressBar value={pct} max={100} showPercentage={false} customColor={color} height={height} glossed />
  </div>
);

const VitalsPanelInner: React.FC<VitalsPanelProps> = ({
  player,
  regenerating = false,
  inCombat = false,
  hudStyle,
}) => {
  // HP/MP come from the vitals store, not `player` (plan 21 §8.3): this panel
  // re-renders when a label changes (a whole point, or a maximum), and nothing
  // above it does. The bars take the exact value at that moment.
  const health = usePlayerVital(player, (v) => Math.round(v.health));
  const maxHealth = usePlayerVital(player, (v) => v.maxHealth);
  const mana = usePlayerVital(player, (v) => Math.round(v.mana));
  const maxMana = usePlayerVital(player, (v) => v.maxMana);
  const exact = readPlayerVitals(player);
  const healthPercentage = Math.max(0, Math.min(100, (exact.health / maxHealth) * 100));
  const manaPercentage = Math.max(0, Math.min(100, (exact.mana / maxMana) * 100));
  const xpGoal = player.level * 100;
  const xpPercentage = Math.max(0, Math.min(100, (player.xp / xpGoal) * 100));

  if (hudStyle) {
    return (
      <>
        {/* HP vital row */}
        <div
          className="hudt-vital player-frame-hp"
          data-kind="hp"
          data-regen={regenerating || undefined}
        >
          <div className="hudt-vital-row">
            <span className="hudt-vital-label">
              <HudGlyph kind="hp" />
              <span>HP</span>
            </span>
            {inCombat && <CombatChip />}
            {regenerating && (
              <span
                className="hudt-chip player-frame-regen"
                data-tone="regen"
                data-testid="player-frame-regen"
              >
                + regenerating
              </span>
            )}
            <span className="hudt-num">
              <b>{health}</b>
              <i>/</i>
              <span>{Math.round(maxHealth)}</span>
            </span>
          </div>
          <span className="hudt-bar player-frame-hp-bar" data-kind="hp">
            <span className="hudt-bar-fill" style={fill(health / maxHealth)} />
          </span>
        </div>

        {/* MP vital row */}
        <div className="hudt-vital" data-kind="mp">
          <div className="hudt-vital-row">
            <span className="hudt-vital-label">
              <HudGlyph kind="mp" />
              <span>MP</span>
            </span>
            <span className="hudt-num">
              <b>{mana}</b>
              <i>/</i>
              <span>{Math.round(maxMana)}</span>
            </span>
          </div>
          <span className="hudt-bar" data-kind="mp">
            <span className="hudt-bar-fill" style={fill(mana / maxMana)} />
          </span>
        </div>

        {/* XP vital row */}
        <div className="hudt-vital" data-kind="xp">
          <div className="hudt-vital-row">
            <span className="hudt-vital-label">
              <HudGlyph kind="xp" />
              <span>XP</span>
            </span>
            <span className="hudt-num">
              <b>{Math.round(player.xp)}</b>
              <i>/</i>
              <span>{xpGoal}</span>
            </span>
          </div>
          <span className="hudt-bar" data-kind="xp">
            <span className="hudt-bar-fill" style={fill(player.xp / xpGoal)} />
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={sectionStyle} className="player-frame-hp" data-regen={regenerating ? 'true' : 'false'}>
        <div style={headerStyle}>
          <div style={labelStyle}>
            <span aria-hidden>❤️</span>
            <span>HP</span>
            {inCombat && <CombatChip />}
            {regenerating && <span className="player-frame-regen" data-testid="player-frame-regen">+ regenerating</span>}
          </div>
          <div style={valuesStyle}>
            <span>{health}</span>
            <span style={{ color: COLORS.textMuted }}>/</span>
            <span style={{ color: COLORS.textSecondary }}>{Math.round(maxHealth)}</span>
          </div>
        </div>
        <div className="player-frame-hp-bar">
          <ProgressBar value={healthPercentage} max={100} showPercentage={false} customColor={COLORS.health} height={12} glossed />
        </div>
      </div>

      <ResourceRow icon="🔵" label="MP" current={mana} max={Math.round(maxMana)} pct={manaPercentage} color={COLORS.mana} height={12} />
      <ResourceRow icon="⭐" label="XP" current={Math.round(player.xp)} max={player.level * 100} pct={xpPercentage} color={COLORS.xp} height={6} />
    </>
  );
};

/**
 * LRG.24 (2026-05-13) — VitalsPanel re-renders only when what it shows
 * changes. Current HP/MP and their maxima come from the vitals store through
 * `usePlayerVital`, which re-renders this panel alone when a label changes;
 * the props supply them only until a server has sent any.
 */
function vitalsEqual(prev: VitalsPanelProps, next: VitalsPanelProps): boolean {
  const a = prev.player;
  const b = next.player;
  const va = readPlayerVitals(a);
  const vb = readPlayerVitals(b);
  return (
    (a.id || a._id) === (b.id || b._id) &&
    Math.round(va.health) === Math.round(vb.health) &&
    va.maxHealth === vb.maxHealth &&
    Math.round(va.mana) === Math.round(vb.mana) &&
    va.maxMana === vb.maxMana &&
    Math.round(a.xp) === Math.round(b.xp) &&
    a.level === b.level &&
    Boolean(prev.regenerating) === Boolean(next.regenerating) &&
    Boolean(prev.inCombat) === Boolean(next.inCombat) &&
    prev.hudStyle === next.hudStyle
  );
}

export const VitalsPanel = React.memo(VitalsPanelInner, vitalsEqual);
VitalsPanel.displayName = 'VitalsPanel';
